import React, { useState, useEffect, useRef, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import Header from './components/Header';
import DrinkCard from './components/DrinkCard';
import SuccessModal from './components/SuccessModal';
import GiftModal from './components/GiftModal';
import Login from './components/Login';
import RechargeModal from './components/RechargeModal';
import GiveRoundModal from './components/GiveRoundModal';
import TransactionHistory from './components/TransactionHistory';
import InviteMemberModal from './components/InviteMemberModal';
import AccountModal from './components/AccountModal';
import AdminPage from './components/AdminPage';
import StatisticsPage from './components/StatisticsPage';
import NotificationPopup from './components/NotificationPopup';
import BottomNav from './components/BottomNav';
import Register from './components/Register';
import { apiService } from './services/api';
import './index.css';

/** Kurze Sperre nach Laden / nach Schließen des Erfolgs-Modals — verhindert Doppelklick durch „Prost“ + Auto-Close */
const DRINK_BOOKING_COOLDOWN_MS = 1500;

/** Fallback, falls ka-ching.mp3 fehlt oder der Browser HTML-Audio blockiert */
function playSynthKaChing() {
  try {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return;
    const ctx = new AC();
    const go = () => {
      const t0 = ctx.currentTime;
      [
        [523.25, 0],
        [659.25, 0.07],
        [783.99, 0.14],
      ].forEach(([freq, delay]) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'triangle';
        osc.frequency.value = freq;
        osc.connect(gain);
        gain.connect(ctx.destination);
        const s = t0 + delay;
        gain.gain.setValueAtTime(0.0001, s);
        gain.gain.exponentialRampToValueAtTime(0.12, s + 0.015);
        gain.gain.exponentialRampToValueAtTime(0.0001, s + 0.18);
        osc.start(s);
        osc.stop(s + 0.2);
      });
      setTimeout(() => ctx.close().catch(() => {}), 500);
    };
    if (ctx.state === 'suspended') {
      ctx.resume().then(go).catch(() => ctx.close().catch(() => {}));
    } else {
      go();
    }
  } catch (_) {
    /* ignore */
  }
}

function App() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [member, setMember] = useState(null);
  const [drinks, setDrinks] = useState([]);
  const [activeGift, setActiveGift] = useState(null);
  const [memberReceivedGift, setMemberReceivedGift] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showGiftModal, setShowGiftModal] = useState(false);
  const [showRechargeModal, setShowRechargeModal] = useState(false);
  const [showGiveRoundModal, setShowGiveRoundModal] = useState(false);
  const [showTransactionHistory, setShowTransactionHistory] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showAccountModal, setShowAccountModal] = useState(false);
  const [showAdminPage, setShowAdminPage] = useState(false);
  const [showStatisticsPage, setShowStatisticsPage] = useState(false);
  const [pendingPopups, setPendingPopups] = useState([]);
  const [giveRoundInitialType, setGiveRoundInitialType] = useState(null);
  const [activeTab, setActiveTab] = useState('home');
  const [successMessage, setSuccessMessage] = useState('');
  const kaChingAudio = useRef(null);
  const drinkCooldownTimerRef = useRef(null);
  const [drinkPurchaseCooldown, setDrinkPurchaseCooldown] = useState(false);

  const startDrinkPurchaseCooldown = useCallback(() => {
    if (drinkCooldownTimerRef.current) {
      window.clearTimeout(drinkCooldownTimerRef.current);
    }
    setDrinkPurchaseCooldown(true);
    drinkCooldownTimerRef.current = window.setTimeout(() => {
      setDrinkPurchaseCooldown(false);
      drinkCooldownTimerRef.current = null;
    }, DRINK_BOOKING_COOLDOWN_MS);
  }, []);

  useEffect(() => {
    return () => {
      if (drinkCooldownTimerRef.current) {
        window.clearTimeout(drinkCooldownTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    checkAuthStatus();
  }, []);

  /** Nach erstem Laden der Hauptansicht: kurz keine Buchung (gleiches Muster wie nach Erfolgs-Modal) */
  useEffect(() => {
    if (isLoading || !isLoggedIn) return;
    startDrinkPurchaseCooldown();
  }, [isLoading, isLoggedIn, startDrinkPurchaseCooldown]);

  /** Audio nach erstem Tipp freischalten (sonst blockiert der Browser oft play() nach API-Aufruf). */
  useEffect(() => {
    if (!isLoggedIn) return undefined;
    const unlock = () => {
      const el = kaChingAudio.current;
      if (el) {
        const v = el.volume;
        const wasMuted = el.muted;
        el.muted = true;
        el.volume = 0;
        el.play()
          .then(() => {
            el.pause();
            el.currentTime = 0;
            el.muted = wasMuted;
            el.volume = v;
          })
          .catch(() => {
            el.muted = wasMuted;
            el.volume = v;
          });
      }
      window.removeEventListener('pointerdown', unlock, true);
    };
    window.addEventListener('pointerdown', unlock, true);
    return () => window.removeEventListener('pointerdown', unlock, true);
  }, [isLoggedIn]);

  const checkAuthStatus = async () => {
    try {
      const response = await apiService.getHomeData();
      
      if (response.success) {
        setIsLoggedIn(true);
        setMember(response.member);
        setDrinks(response.drinks);
        setActiveGift(response.active_gift);
        setMemberReceivedGift(response.member_received_gift || false);
        setPendingPopups(Array.isArray(response.popups) ? response.popups : []);
      } else {
        setIsLoggedIn(false);
        setMember(null);
        setDrinks([]);
        setActiveGift(null);
        setMemberReceivedGift(false);
        setPendingPopups([]);
      }
    } catch (error) {
      setIsLoggedIn(false);
      setMember(null);
      setDrinks([]);
        setActiveGift(null);
        setMemberReceivedGift(false);
        setPendingPopups([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleNotificationPopupClose = () => {
    setPendingPopups((prev) => prev.slice(1));
  };

  /** Ka-Ching nur, wenn Soundeffekte in den Konto-Einstellungen nicht aus sind. */
  const playKaChingSound = () => {
    if (!member || member.sound_enabled === false) return;
    const el = kaChingAudio.current;
    if (el) {
      el.currentTime = 0;
      const p = el.play();
      if (p !== undefined) {
        p.catch(() => {
          playSynthKaChing();
        });
      }
    } else {
      playSynthKaChing();
    }
  };

  const handlePurchase = async (drinkId) => {
    if (drinkPurchaseCooldown) return;
    try {
      const result = await apiService.createTransaction(drinkId);
      if (result.success) {
        playKaChingSound();
        setSuccessMessage(`Getränk erfolgreich gekauft!`);
        setShowSuccessModal(true);
        // Update member balance
        setMember(prev => ({
          ...prev,
          balance: result.new_balance
        }));
        // Update member received gift status
        if (result.member_received_gift !== undefined) {
          setMemberReceivedGift(result.member_received_gift);
        }
      }
    } catch (error) {
      console.error('Fehler beim Kauf:', error);
    }
  };

  const handleSuccessModalClose = () => {
    setShowSuccessModal(false);
    startDrinkPurchaseCooldown();
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    
    // Close all modals when switching tabs
    setShowRechargeModal(false);
    setShowGiveRoundModal(false);
    setShowTransactionHistory(false);
    setShowAccountModal(false);
    setShowInviteModal(false);
    
    // Open specific modals based on tab
    switch (tab) {
      case 'recharge':
        setShowRechargeModal(true);
        break;
      case 'gift':
        setGiveRoundInitialType(null);
        setShowGiveRoundModal(true);
        break;
      case 'history':
        setShowTransactionHistory(true);
        break;
      case 'account':
        setShowAccountModal(true);
        break;
      default:
        break;
    }
  };

  // Main App Component
  const MainApp = () => {
    if (showAdminPage) {
      return <AdminPage user={member} onBack={() => setShowAdminPage(false)} />;
    }

    return (
      <div className="App">
        <Header 
          member={member} 
          onShowAdmin={() => setShowAdminPage(true)}
          onToggleStatistics={() => setShowStatisticsPage((v) => !v)}
          statisticsOpen={showStatisticsPage}
        />
        
        <main className="flex-1 overflow-y-auto pb-24">
          <div
            className={
              showStatisticsPage
                ? 'container mx-auto px-4 pt-1 pb-4'
                : 'container mx-auto px-4 py-6'
            }
          >
            {showStatisticsPage ? (
              <StatisticsPage embedded />
            ) : (
              <>
                <div className="mb-6">
                  <div className="bg-accent-3 rounded-lg shadow-md p-5">
                    <h2 className="text-2xl font-heading font-bold text-bg-1 mb-2">
                      Hallo, {member?.user_first_name || member?.user_username}! 🍺
                    </h2>
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-bg-1 font-medium">Guthaben:</span>
                      <span className={`text-2xl font-bold ${
                        parseFloat(member?.balance || 0) < 0 ? 'text-status-error' : 'text-text-success'
                      }`}>
                        {parseFloat(member?.balance || 0).toFixed(2)} €
                      </span>
                    </div>
                    {activeGift && !(activeGift.type === 'next_round' && memberReceivedGift) && (
                      <div className="bg-gradient-to-r from-accent-1/20 to-accent-2/20 border-2 border-accent-1 rounded-lg p-3 mt-3">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-2">
                            <span className="text-2xl">🎁</span>
                            <div>
                              <div className="text-bg-1 font-semibold text-sm">
                                {activeGift.type === 'next_round' ? 'Nächste Runde' : 'Ganzer Abend'}
                              </div>
                              <div className="text-bg-1/70 text-xs">
                                Von {activeGift.giver_name}
                              </div>
                            </div>
                          </div>
                          {activeGift.message && (
                            <div className="text-bg-1/80 text-xs italic max-w-32 truncate">
                              "{activeGift.message}"
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                <div className="mb-6">
                  <h3 className="text-lg font-heading font-bold text-text-strong mb-4">Getränke</h3>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {drinks.map(drink => (
                      <DrinkCard
                        key={drink.id}
                        drink={drink}
                        onPurchase={() => handlePurchase(drink.id)}
                        memberBalance={member?.balance}
                        disabled={drinkPurchaseCooldown}
                      />
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>
        </main>

        <BottomNav 
          activeTab={activeTab} 
          onTabChange={handleTabChange}
          showInviteButton={member?.is_superuser}
          onShowInvite={() => setShowInviteModal(true)}
        />

        <SuccessModal
          isOpen={showSuccessModal}
          onClose={handleSuccessModalClose}
          message={successMessage}
        />

        <GiftModal
          isOpen={showGiftModal}
          onClose={() => setShowGiftModal(false)}
        />

        <RechargeModal
          isOpen={showRechargeModal}
          onClose={() => {
            setShowRechargeModal(false);
            setActiveTab('home');
          }}
          member={member}
          onRechargeSuccess={(newBalance) => {
            setMember(prev => ({ ...prev, balance: newBalance }));
          }}
        />

        <GiveRoundModal
          isOpen={showGiveRoundModal}
          initialGiftType={giveRoundInitialType}
          onClose={() => {
            setShowGiveRoundModal(false);
            setGiveRoundInitialType(null);
            setActiveTab('home');
          }}
          member={member}
          activeGift={activeGift}
          memberReceivedGift={memberReceivedGift}
          onGiftSuccess={(gift) => {
            setActiveGift(gift);
            if (!gift) {
              setMemberReceivedGift(false);
            }
            if (gift && gift.type === 'full_evening') {
              setPendingPopups((prev) => prev.filter((p) => p.type !== 'birthday_treat'));
            }
          }}
        />

        <TransactionHistory
          isOpen={showTransactionHistory}
          onClose={() => {
            setShowTransactionHistory(false);
            setActiveTab('home');
          }}
          onTransactionCancelled={(newBalance) => {
            setMember(prev => ({
              ...prev,
              balance: newBalance
            }));
          }}
        />

        <InviteMemberModal
          isOpen={showInviteModal}
          onClose={() => {
            setShowInviteModal(false);
            setActiveTab('home');
          }}
        />

        <AccountModal
          isOpen={showAccountModal}
          onClose={() => {
            setShowAccountModal(false);
            setActiveTab('home');
          }}
          member={member}
          onProfileUpdate={(updatedMember) => setMember(updatedMember)}
        />

        {pendingPopups.length > 0 && (
          <NotificationPopup
            type={pendingPopups[0].type}
            onClose={handleNotificationPopupClose}
            onBirthdayTreatGiveRound={() => {
              setGiveRoundInitialType('full_evening');
              setShowGiveRoundModal(true);
            }}
          />
        )}
      </div>
    );
  };

  // Loading Screen
  if (isLoading) {
    return (
      <div className="min-h-screen bg-accent-3 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-accent-1 border-t-transparent mx-auto mb-4"></div>
          <div className="text-bg-1 text-lg font-semibold">Wachtel Stammtisch</div>
          <div className="text-bg-1/70 text-sm">Lade...</div>
        </div>
      </div>
    );
  }

  const publicUrl = process.env.PUBLIC_URL || '';

  return (
    <Router>
      <>
        {isLoggedIn && (
          <audio
            ref={kaChingAudio}
            preload="auto"
            playsInline
            className="fixed w-px h-px opacity-0 pointer-events-none"
            aria-hidden
          >
            <source src={`${publicUrl}/ka-ching.mp3`} type="audio/mpeg" />
          </audio>
        )}
        <Routes>
          <Route path="/register/:token" element={<Register />} />
          <Route
            path="/"
            element={isLoggedIn ? <MainApp /> : <Login onLoginSuccess={checkAuthStatus} />}
          />
          <Route
            path="*"
            element={isLoggedIn ? <MainApp /> : <Login onLoginSuccess={checkAuthStatus} />}
          />
        </Routes>
      </>
    </Router>
  );
}

export default App; 